import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Button57 from '../components/Button57';

const { FiAward, FiStar, FiHeart } = FiIcons;

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 }
  };

  const values = [
    {
      icon: FiAward,
      title: 'Culinary Mastery',
      desc: 'Our chefs bring decades of experience in traditional Korean and Japanese techniques.'
    },
    {
      icon: FiHeart,
      title: 'Ethical Sourcing',
      desc: 'We partner with premium suppliers to ensure the freshest seasonal ingredients.'
    },
    {
      icon: FiStar,
      title: 'Premium Quality',
      desc: 'We never compromise on the standards of our ingredients, sourcing only the finest for our table.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - Ganda Sushi | Luxury Korean & Japanese Dining</title>
        <meta name="description" content="Discover the story behind Ganda Sushi, where Korean tradition meets Japanese precision in the heart of Jamaica, Queens." />
      </Helmet>

      <main className="bg-luxury-cream text-luxury-dark min-h-screen">
        {/* Header Hero */}
        <section className="pt-48 pb-32 text-center px-6">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
            <span className="text-luxury-gold tracking-[0.5em] uppercase text-xs font-bold mb-8 block">Our Heritage</span>
            <h1 className="text-6xl md:text-9xl font-serif mb-12 leading-tight">The Ganda Story</h1>
            {/* Darkened text color */}
            <p className="text-luxury-dark/80 text-xl max-w-2xl mx-auto leading-relaxed font-light">
              A sanctuary for food lovers where the bold spirit of Korean cuisine 
              meets the delicate artistry of Japanese tradition.
            </p>
          </motion.div>
        </section>

        {/* Narrative Section */}
        <section className="py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Updated rounded corners to rounded-2xl */}
              <div className="aspect-[4/5] overflow-hidden rounded-2xl shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1553621042-f6e147245754?auto=format&fit=crop&w=1000&q=80" 
                  alt="Chef Crafting Sushi" 
                  className="w-full h-full object-cover transition-all duration-1000 transform"
                />
              </div>
              <div className="absolute -bottom-10 -right-10 w-2/3 h-2/3 border border-luxury-gold/20 -z-10 rounded-2xl" />
            </motion.div>

            <motion.div {...fadeInUp}>
              <h2 className="text-4xl md:text-6xl font-serif mb-10 leading-snug">Precision. Passion. <br/><span className="text-luxury-gold">Provenance.</span></h2>
              {/* Darkened text color from /70 to /90 */}
              <div className="space-y-8 text-luxury-dark/90 leading-relaxed text-lg font-light">
                <p>
                  At Ganda, we believe that true luxury is found in the purity of ingredients and the integrity of the process. Every dish is a dialogue between tradition and innovation.
                </p>
                <p>
                  What began as a small family vision in Jamaica, Queens has evolved into a destination for those who appreciate the finer nuances of Asian fusion. We don't just serve food; we curate moments of culinary discovery.
                </p>
                <div className="pt-12 flex justify-center lg:justify-start">
                  <Button57 
                    to="/menu" 
                    text="View Our Full Menu" 
                    altText="Discover Flavors"
                    className="px-16"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-40 bg-luxury-cream">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-20">
              {values.map((v, i) => (
                <motion.div 
                  key={i}
                  {...fadeInUp}
                  transition={{ delay: i * 0.2 }}
                  className="text-center group"
                >
                  <div className="w-20 h-20 mx-auto mb-10 border border-luxury-gold/30 rounded-full flex items-center justify-center text-luxury-gold group-hover:bg-luxury-gold group-hover:text-white transition-all duration-500 shadow-lg bg-white">
                    <SafeIcon icon={v.icon} className="w-10 h-10" />
                  </div>
                  <h3 className="text-2xl font-serif mb-6 text-luxury-dark">{v.title}</h3>
                  {/* Darkened text color from /50 to /75 */}
                  <p className="text-luxury-dark/75 leading-relaxed font-light">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing Section */}
        <section className="py-48 text-center bg-white border-t border-luxury-gold/10">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto px-6">
            <h2 className="text-5xl md:text-7xl font-serif mb-12">Visit Our Kitchen</h2>
            {/* Darkened text color from /60 to /85 */}
            <p className="text-luxury-dark/85 mb-16 text-xl font-light">
              Experience the artistry of Ganda Sushi in person. <br/>
              We look forward to welcoming you to our table.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-8">
              <Button57 
                href="tel:+17183801818" 
                text="Book a Table" 
                altText="Call Now"
                className="px-12"
              />
              <Button57 
                to="/contact" 
                text="Get Directions" 
                altText="Find Us"
                className="px-12"
              />
            </div>
          </motion.div>
        </section>
      </main>
    </>
  );
};

export default About;